import React from "react";
import { classNames } from "repoV2/utils/common/render/classNames";
import { colorWithAlpha } from "repoV2/features/Common/modules/Colors/utils/rgbColors.utils";
import { LISTING_TYPES } from "repoV2/features/Listing/Listing.constants";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import commonStyles from "repoV2/features/CheckoutPages/modules/CheckoutAccordion/CheckoutAccordion.module.scss";
import { IFormGroupWrapperProps } from "../CheckoutAccordion/CheckoutAccordion.interfaces";
import CheckoutAccordion from "../CheckoutAccordion/CheckoutAccordion";
import SessionDetailsFormGroupHeader from "../PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/modules/SessionDetailsFormGroupHeader/SessionDetailsFormGroupHeader";
import { useWebinarSessionsBaseProps } from "../PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/modules/WebinarSessions/WebinarSession.utils";
import { checkout_form } from "../PaymentDetailsSection/modules/CheckoutForm/CheckoutForm.constants";
import { useCheckoutThemeContext } from "../CheckoutThemeContextProvider";
import SingleSession from "../PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/modules/WebinarSessions/modules/SingleSession/SingleSessions";
import WorkshopSessions from "../PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/modules/WorkshopSessions/WorkshopSessions";
import styles from "./SessionInfoCard.module.scss";
import { useSessionDetailsFormGroup } from "../PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/SessionDetailsFormGroup.utils";
import ClassesSessions from "../PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/modules/ClassesSessions/ClassesSessions";

const Root = ({
    children,
    classesCount,
}: {
    children: IFormGroupWrapperProps["children"];
    classesCount: number;
}) => {
    const {
        colors: { color1 = "" },
    } = useCheckoutThemeContext();

    const colorVariablesStyle = `--checkout-session-info-color: ${colorWithAlpha(
        color1,
        0.1
    )};`;

    return (
        <>
            <style>{`:root {
                        ${colorVariablesStyle}
                    }`}</style>
            <CheckoutAccordion
                showExpandIcon={false}
                title={
                    <SessionDetailsFormGroupHeader
                        timezoneFieldName={
                            checkout_form.timezone_details.field_names
                                .timezone_data_description_section
                        }
                        classesCount={classesCount}
                    />
                }
                className={classNames(
                    commonStyles.Wrapper,
                    styles.sessionContainerRoot
                )}
                expanded
                classes={{
                    root: commonStyles.formGroupRoot,
                    disabled: commonStyles.formGroupDisabled,
                }}
                headerClasses={{
                    root: classNames(
                        commonStyles.headerRoot,
                        styles.headerRoot
                    ),
                    content: styles.headerContent,
                    disabled: commonStyles.formGroupDisabled,
                    expanded: commonStyles.formGroupExpanded,
                }}
                detailsClasses={{
                    root: styles.detailsRoot,
                }}
            >
                {children}
            </CheckoutAccordion>
        </>
    );
};

const SessionInfoCard = () => {
    const webinarSessionsBaseProps = useWebinarSessionsBaseProps();

    const { listingType } = useCheckoutBasicDetailsContext();

    let SessionSelectorComponent: JSX.Element;

    const {
        classesCount,
        classes,
        isEmptyClasses,
        maxSpots = 10000,
    } = useSessionDetailsFormGroup();

    switch (listingType) {
        case LISTING_TYPES.WEBINAR: {
            const { hide: hideWebinarSessions, sessionsCount } =
                webinarSessionsBaseProps;
            if (hideWebinarSessions || sessionsCount > 1) return null;
            SessionSelectorComponent = (
                <SingleSession {...webinarSessionsBaseProps} />
            );
            break;
        }
        case LISTING_TYPES.WORKSHOPS: {
            const { hide: hideWebinarSessions } = webinarSessionsBaseProps;
            if (hideWebinarSessions) return null;
            SessionSelectorComponent = (
                <WorkshopSessions {...webinarSessionsBaseProps} />
            );
            break;
        }
        case LISTING_TYPES.ROLLING_CLASSES:
        case LISTING_TYPES.GROUP_CLASSES:
        case LISTING_TYPES.ONE_ON_ONE_CLASSES: {
            if (isEmptyClasses || classesCount !== 1) return null;
            SessionSelectorComponent = (
                <ClassesSessions
                    hide={isEmptyClasses}
                    classes={classes}
                    classesCount={classesCount}
                    maxSpots={maxSpots}
                />
            );

            break;
        }
        default:
            return null;
    }

    if (!SessionSelectorComponent) return null;
    return <Root classesCount={classesCount}>{SessionSelectorComponent}</Root>;
};

export default SessionInfoCard;
